import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCaptions } from '../../context/CaptionsContext';
import { useLanguage } from '../../context/LanguageContext';
import './Captions.css';

const LABELS = {
  en: { title: 'Narration Transcript', play: 'Play', pause: 'Pause', restart: 'Restart', close: 'Close' },
  mr: { title: 'निवेदनाचा मजकूर', play: 'चालू करा', pause: 'थांबवा', restart: 'पुन्हा सुरू', close: 'बंद' },
  hi: { title: 'वर्णन का पाठ', play: 'चलाएँ', pause: 'रोकें', restart: 'पुनः प्रारंभ', close: 'बंद' },
};

const fmt = (s) => {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

const TranscriptDrawer = () => {
  const { language } = useLanguage();
  const l = LABELS[language] || LABELS.en;
  const {
    drawerOpen, setDrawerOpen, segments, currentSegmentIndex,
    currentTime, totalDuration, playing, play, pause, seek, restart,
  } = useCaptions();

  const listRef = useRef(null);
  const activeRef = useRef(null);

  // Auto-scroll current line into view.
  useEffect(() => {
    if (!drawerOpen) return;
    if (activeRef.current && listRef.current) {
      activeRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [drawerOpen, currentSegmentIndex]);

  // ESC to close.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, setDrawerOpen]);

  if (!drawerOpen) return null;

  const pct = totalDuration > 0
    ? Math.min(100, (currentTime / totalDuration) * 100)
    : 0;

  const node = (
    <div className="cap-drawer-root" role="dialog" aria-modal="false" aria-label={l.title}>
      <div className="cap-drawer">
        <div className="cap-drawer__header">
          <h3 className="cap-drawer__title">📝 {l.title}</h3>
          <button
            type="button"
            className="cap-drawer__close"
            onClick={() => setDrawerOpen(false)}
            aria-label={l.close}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor"
                strokeWidth="2.2" strokeLinecap="round" fill="none" />
            </svg>
          </button>
        </div>

        <div className="cap-drawer__controls">
          <button
            type="button"
            className="cap-drawer__playbtn"
            onClick={() => (playing ? pause() : play())}
            aria-label={playing ? l.pause : l.play}
            title={playing ? l.pause : l.play}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <rect x="7"  y="5" width="3.5" height="14" rx="1" fill="currentColor" />
                <rect x="13.5" y="5" width="3.5" height="14" rx="1" fill="currentColor" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M7 5 L19 12 L7 19 Z" fill="currentColor" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="cap-drawer__iconbtn"
            onClick={restart}
            aria-label={l.restart}
            title={l.restart}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M4 12a8 8 0 1 0 2.34-5.66" stroke="currentColor"
                strokeWidth="2" fill="none" strokeLinecap="round" />
              <path d="M4 4v5h5" stroke="currentColor" strokeWidth="2"
                fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div className="cap-drawer__time">
            {fmt(currentTime)} / {fmt(totalDuration)}
          </div>
        </div>

        <div className="cap-drawer__progress" aria-hidden="true">
          <div className="cap-drawer__progress-fill" style={{ width: `${pct}%` }} />
        </div>

        <div className="cap-drawer__list" ref={listRef}>
          {segments.map((seg, idx) => {
            const isActive = idx === currentSegmentIndex;
            return (
              <button
                type="button"
                key={idx}
                ref={isActive ? activeRef : null}
                onClick={() => seek(seg.start)}
                className={`cap-line${isActive ? ' cap-line--active' : ''}`}
                aria-current={isActive ? 'true' : undefined}
              >
                <span className="cap-line__time">{fmt(seg.start)}</span>
                <span className="cap-line__text">{seg.text}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
};

export default TranscriptDrawer;
